/**
 * T1102 · Platform slot 1 · Storage (web).
 *
 * Web storage slot wires:
 *
 *   - `sync`  → `globalThis.localStorage` (same reference, no wrapper).
 *   - `async` → a thin `AsyncStorageLike` wrapper over `idb-keyval`
 *     (or any injected key-value pair API).
 *
 * `idb-keyval` is imported via a structural shape so this module
 * compiles without taking a direct dep on the package. Production
 * wiring passes `{ get: idbGet, set: idbSet, del: idbDel }`.
 */

import type { AsyncStorageLike, StorageSlot, SyncStorageLike } from '../adapter.js';

export interface IdbKv {
  get<T = string>(key: string): Promise<T | undefined>;
  set(key: string, value: string): Promise<void>;
  del(key: string): Promise<void>;
}

export function createIdbAsyncStorage(idb: IdbKv): AsyncStorageLike {
  return {
    async getItem(key: string): Promise<string | null> {
      const v = await idb.get<string>(key);
      return v ?? null;
    },
    async setItem(key: string, value: string): Promise<void> {
      await idb.set(key, value);
    },
    async removeItem(key: string): Promise<void> {
      await idb.del(key);
    },
  };
}

export interface WebStorageDeps {
  readonly idb: IdbKv;
  readonly localStorage?: SyncStorageLike;
}

export function createWebStorageSlot(deps: WebStorageDeps): StorageSlot {
  const sync =
    deps.localStorage ??
    (typeof globalThis !== 'undefined' &&
    'localStorage' in globalThis &&
    (globalThis as { localStorage?: unknown }).localStorage !== undefined
      ? (globalThis as { localStorage: SyncStorageLike }).localStorage
      : undefined);
  if (sync === undefined) {
    throw new Error(
      'createWebStorageSlot: globalThis.localStorage unavailable; pass deps.localStorage explicitly.',
    );
  }
  return {
    sync,
    async: createIdbAsyncStorage(deps.idb),
  };
}
